
import React, { useState, useEffect, useRef, FC } from "react";
import { UserProfile, Organization, AcademicYear, Gender } from '../types';
import { useTranslate } from '../hooks/useTranslate';
import { getGreeting, sanitizePersonName, sanitizePhoneNumber, compressImage, detectUserCountry, getCountryFromPhone } from '../lib/helpers';
import { validateChatAttachmentFile, validateAttachmentFile } from '../firebaseUtils';
import { LANGUAGES, COUNTRY_CODES } from "../lib/constants";
import { ai, generateContentWithRetry } from "../lib/gemini";

export const ProgressRing = ({ percentage }: { percentage: number }) => {
    const radius = 50;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (percentage / 100) * circumference;

    return (
        <svg className="w-full h-full" viewBox="0 0 120 120">
            <circle
                className="text-gray-200"
                strokeWidth="10"
                stroke="currentColor"
                fill="transparent"
                r={radius}
                cx="60"
                cy="60"
            />
            <circle
                className="text-green-500"
                strokeWidth="10"
                strokeDasharray={circumference}
                strokeDashoffset={offset}
                strokeLinecap="round"
                stroke="currentColor"
                fill="transparent"
                r={radius}
                cx="60"
                cy="60"
                transform="rotate(-90 60 60)"
            />
            <text x="50%" y="50%" textAnchor="middle" dy=".3em" className="text-2xl font-bold fill-current text-gray-700">
                {`${Math.round(percentage)}%`}
            </text>
        </svg>
    );
};

export const Avatar = ({ photoUrl, name, size = 'md', unread = false }: { photoUrl?: string; name: string; size?: 'sm' | 'md' | 'lg', unread?: boolean }) => {
    const sizeClasses = {
        sm: 'w-8 h-8 text-xs',
        md: 'w-12 h-12 text-lg',
        lg: 'w-24 h-24 text-2xl'
    };
    const getInitials = (name: string) => {
        if (!name) return '';
        const names = name.split(' ');
        if (names.length > 1) {
            return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    };

    return (
        <div className={`relative rounded-full flex items-center justify-center bg-indigo-100 text-indigo-500 font-bold overflow-hidden flex-shrink-0 ${sizeClasses[size]}`}>
            {photoUrl ? (
                <img src={photoUrl} alt={name} className="w-full h-full object-cover" />
            ) : (
                <span>{getInitials(name)}</span>
            )}
            {unread && (
                <span className="absolute top-0 right-0 block h-3 w-3 rounded-full bg-red-500 ring-2 ring-white" />
            )}
        </div>
    );
};

export const LanguageSelector = ({ selectedLanguage, onSelectLanguage }: { selectedLanguage: string, onSelectLanguage: (langCode: string) => void }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);

    const selectedLanguageLabel = LANGUAGES.find(l => l.code === selectedLanguage)?.label || 'Select Language';

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const filteredLanguages = LANGUAGES.filter(lang =>
        lang.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleSelect = (langCode: string) => {
        onSelectLanguage(langCode);
        setIsOpen(false);
        setSearchTerm('');
    };

    return (
        <div className="relative w-full" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full p-3 border-2 border-gray-200 rounded-lg bg-white text-left flex justify-between items-center"
            >
                <span>{selectedLanguageLabel}</span>
                <span className={`material-icons-round transition-transform ${isOpen ? 'rotate-180' : ''}`}>expand_more</span>
            </button>
            {isOpen && (
                <div className="absolute top-full mt-1 w-full bg-white border-2 border-gray-200 rounded-lg shadow-lg z-20">
                    <div className="p-2">
                        <input
                            type="text"
                            placeholder="Search language..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className="w-full p-2 border-b-2 border-gray-200 outline-none"
                            autoFocus
                        />
                    </div>
                    <ul className="max-h-60 overflow-y-auto">
                        {filteredLanguages.map(lang => (
                            <li key={lang.code}>
                                <button
                                    onClick={() => handleSelect(lang.code)}
                                    className="w-full text-left px-4 py-2 hover:bg-gray-100"
                                >
                                    {lang.label}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export const ImageUpload = ({ onImageUpload, currentImage, label = "Your Photo", required = false }: { onImageUpload: (dataUrl: string) => void; currentImage?: string; label?: string; required?: boolean; }) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            const validation = validateChatAttachmentFile(file);
            if (!validation.valid) {
                alert(validation.error);
                if (event.target) event.target.value = '';
                return;
            }

            try {
                const compressed = await compressImage(file, 400, 0.8);
                onImageUpload(compressed);
            } catch (err) {
                const reader = new FileReader();
                reader.onload = (e) => {
                    onImageUpload(e.target?.result as string);
                };
                reader.readAsDataURL(file);
            }
        }
    };

    return (
        <div className="flex flex-col items-center mb-4">
            <label className="text-xs font-bold text-gray-500 uppercase mb-2 flex items-center gap-1">
                <span>{label}</span>
                {required && <span className="text-red-500 font-bold">*</span>}
            </label>
            <div className={`relative w-24 h-24 bg-gray-100 rounded-full border-2 ${required && !currentImage ? 'border-dashed border-indigo-400 hover:border-indigo-600' : 'border-gray-200'} flex items-center justify-center overflow-hidden cursor-pointer transition-colors shadow-sm`}>
                {currentImage ? (
                    <img src={currentImage} alt="Preview" className="w-full h-full object-cover" />
                ) : (
                    <div className="flex flex-col items-center justify-center text-gray-400">
                        <span className="material-icons-round text-3xl">add_a_photo</span>
                        <span className="text-[10px] font-semibold mt-0.5">{required ? 'Required' : 'Upload'}</span>
                    </div>
                )}
                <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="absolute inset-0 w-full h-full bg-black/0 hover:bg-black/20 flex items-center justify-center transition-colors group"
                >
                    <span className="material-icons-round text-white opacity-0 group-hover:opacity-100">edit</span>
                </button>
            </div>
            <input type="file" accept="image/*" ref={fileInputRef} onChange={handleFileChange} className="hidden" />
        </div>
    );
};

export const PhoneInput = ({ 
    value, 
    onChange, 
    label = "Phone Number",
    defaultCountryCode,
    onCountryCodeChange
}: { 
    value: string; 
    onChange: (value: string) => void; 
    label?: string;
    defaultCountryCode?: string;
    onCountryCodeChange?: (countryCode: string, countryName: string) => void;
}) => {
    // Determine initial default country code
    const initialDefault = defaultCountryCode || detectUserCountry().code;
    const [countryCode, setCountryCode] = useState(initialDefault);
    const [number, setNumber] = useState('');

    useEffect(() => {
        // Parse initial value to split code and number
        if (value) {
            // Sort by length desc to match longest code first (e.g. +1 vs +123)
            const matchedCode = COUNTRY_CODES
                .slice()
                .sort((a, b) => b.code.length - a.code.length)
                .find(c => value.startsWith(c.code));
            
            if (matchedCode) {
                setCountryCode(matchedCode.code);
                setNumber(value.slice(matchedCode.code.length));
            } else {
                setNumber(value); // Fallback
            }
        } else if (defaultCountryCode) {
            setCountryCode(defaultCountryCode);
        }
    }, [value, defaultCountryCode]);

    // Find current country config for limits
    const currentCountry = COUNTRY_CODES.find(c => c.code === countryCode) || COUNTRY_CODES[0];
    const maxLength = currentCountry.maxLen;
    const minLength = currentCountry.minLen;
    
    // Check validity for visual feedback
    const isValid = number.length >= minLength && number.length <= maxLength;

    const handleCodeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newCode = e.target.value;
        const newCountry = COUNTRY_CODES.find(c => c.code === newCode) || currentCountry;
        setCountryCode(newCode);
        onCountryCodeChange?.(newCode, newCountry.name);
        // Trigger change with existing number and new code
        onChange(number ? `${newCode}${number}` : '');
    };

    const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const rawNumber = e.target.value;
        // Sanitize to only numbers
        const newNumber = sanitizePhoneNumber(rawNumber);
        
        // Enforce max length strictness
        if (newNumber.length <= maxLength) {
            setNumber(newNumber);
            onChange(newNumber ? `${countryCode}${newNumber}` : '');
        }
    };

    return (
        <div>
            <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">{label}</label>
            <div className="flex gap-2 relative">
                <select
                    value={countryCode}
                    onChange={handleCodeChange}
                    className="p-3 border-2 border-gray-200 rounded-lg bg-gray-50 text-sm"
                >
                    {COUNTRY_CODES.map(c => (
                        <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
                    ))}
                </select>
                <input
                    type="tel"
                    value={number}
                    onChange={handleNumberChange}
                    placeholder={`Max ${maxLength} digits`}
                    className={`w-full p-3 border-2 rounded-lg text-sm flex-1 ${!isValid && number.length > 0 ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
                />
            </div>
            {!isValid && number.length > 0 && (
                <p className="text-[10px] text-red-500 mt-1 pl-1">
                    Enter exactly {minLength === maxLength ? maxLength : `${minLength}-${maxLength}`} digits for {currentCountry.name}.
                </p>
            )}
        </div>
    );
};

export const GenderSelect = ({ value, onChange, label = "Gender", options }: { value: Gender | undefined, onChange: (val: Gender) => void, label?: string, options?: Gender[] }) => {
    const opts = options || ['boy', 'girl', 'other'];
    return (
        <div>
            <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">{label}</label>
            <div className="flex gap-2">
                {opts.map(g => (
                    <button
                        key={g}
                        onClick={() => onChange(g)}
                        className={`flex-1 py-2 rounded-lg border-2 text-sm capitalize font-medium transition-all ${value === g ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-gray-200 hover:border-gray-300 text-gray-600'}`}
                    >
                        {g}
                    </button>
                ))}
            </div>
        </div>
    );
};

export const WelcomeBanner = ({ user, org, onEditOrg, children }: { user: UserProfile; org?: Organization, onEditOrg?: () => void, children?: React.ReactNode }) => {
    const greeting = getGreeting();
    
    return (
        <div className="p-4 bg-indigo-600 text-white no-print flex justify-between items-end">
            <div>
                <p className="text-sm font-medium opacity-80">{greeting},</p>
                <h2 className="text-2xl font-bold">{user.name}</h2>
                {org && (
                     <div className="flex items-center gap-2 mt-1">
                        {org.photoUrl && <img src={org.photoUrl} alt={`${org.name} logo`} className="w-5 h-5 rounded-md object-cover" />}
                        <p className="text-xs font-medium opacity-80">{org.name}</p>
                        {onEditOrg && <button onClick={onEditOrg} className="p-1 -m-1 text-white opacity-60 hover:opacity-100 rounded-full"><span className="material-icons-round text-sm">edit</span></button>}
                     </div>
                )}
            </div>
            {children && (
                <div className="mb-1 text-gray-900">
                    {children}
                </div>
            )}
        </div>
    );
};

export const Header = ({ title, subtitle, user, onBack, onLogout, onEditProfile, children, orgLogoUrl, hideProfile = false, onShowHelp, onShowPolicy }: {
    title: React.ReactNode;
    subtitle?: React.ReactNode;
    user: UserProfile;
    onBack?: () => void;
    onLogout: () => void;
    onEditProfile: () => void;
    children?: React.ReactNode;
    orgLogoUrl?: string;
    hideProfile?: boolean;
    onShowHelp?: () => void;
    onShowPolicy?: () => void;
}) => {
    const [showUserMenu, setShowUserMenu] = useState(false);
    const t = useTranslate(user.language);
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setShowUserMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className="p-4 bg-white border-b border-gray-200 flex items-center justify-between no-print">
            <div className="flex items-center gap-2 flex-1">
                {onBack && (
                    <button onClick={onBack} className="p-2 text-gray-500 hover:bg-gray-100 rounded-full">
                        <span className="material-icons-round">arrow_back</span>
                    </button>
                )}
                <div className="flex items-center gap-2">
                    {orgLogoUrl && <img src={orgLogoUrl} alt="Organization Logo" className="w-8 h-8 rounded-md object-cover" />}
                    <div>
                        <h1 className="font-bold text-lg text-gray-800">{title}</h1>
                        {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
                    </div>
                </div>
            </div>
            <div className="flex items-center gap-2">
                {children}
                {!hideProfile && (
                    <div className="relative" ref={menuRef}>
                        <button onClick={() => setShowUserMenu(!showUserMenu)}>
                            <Avatar photoUrl={user.photoUrl} name={user.name} size="sm" />
                        </button>
                        {showUserMenu && (
                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl z-20 py-1">
                                <button onClick={() => { onEditProfile(); setShowUserMenu(false); }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2">
                                    <span className="material-icons-round text-base">edit</span> {t('edit_profile')}
                                </button>
                                {onShowHelp && (
                                    <button onClick={() => { onShowHelp(); setShowUserMenu(false); }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2">
                                        <span className="material-icons-round text-base">help_outline</span> Help
                                    </button>
                                )}
                                {onShowPolicy && (
                                    <button onClick={() => { onShowPolicy(); setShowUserMenu(false); }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2">
                                        <span className="material-icons-round text-base">policy</span> Privacy Policy
                                    </button>
                                )}
                                <button onClick={onLogout} className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 flex items-center gap-2">
                                    <span className="material-icons-round text-base">logout</span> Logout
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export const ImageViewerModal = ({ imageUrl, onClose }: { imageUrl: string, onClose: () => void }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 animate-fade-in" onClick={onClose}>
        <div className="absolute top-4 right-4 flex gap-2 z-10">
            <a 
              href={imageUrl} 
              download 
              className="text-white bg-black bg-opacity-50 p-2 rounded-full"
              onClick={(e) => e.stopPropagation()} 
            >
              <span className="material-icons-round">download</span>
            </a>
            <button onClick={onClose} className="text-white bg-black bg-opacity-50 p-2 rounded-full">
              <span className="material-icons-round">close</span>
            </button>
        </div>
        <img 
            src={imageUrl} 
            alt="Full screen view" 
            className="max-w-[90vw] max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
        />
    </div>
  );
};

export const YearSelector = ({ academicYears, activeYearId, onSelectYear }: {
    academicYears: AcademicYear[];
    activeYearId?: string;
    onSelectYear: (yearId: string) => void;
}) => {
    return (
        <select
            value={activeYearId || ''}
            onChange={e => onSelectYear(e.target.value)}
            className="p-2 text-xs border border-gray-300 rounded-md bg-white"
        >
            {academicYears.sort((a,b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime()).map(year => (
                <option key={year.id} value={year.id}>{year.name}</option>
            ))}
        </select>
    );
};

export const OrgSwitcher = ({ orgs, activeOrgId, onSwitchOrg, user }: {
    orgs: Organization[];
    activeOrgId: string;
    onSwitchOrg: (orgId: string) => void;
    user: UserProfile;
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const activeOrg = orgs.find(o => o.id === activeOrgId);
    const t = useTranslate(user.language);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    if (orgs.length <= 1) {
        return null;
    }

    return (
        <div ref={menuRef} className="relative">
            <button onClick={() => setIsOpen(!isOpen)} className="flex items-center gap-1 p-1 rounded-lg hover:bg-gray-100">
                {activeOrg?.photoUrl ? (
                    <img src={activeOrg.photoUrl} alt={activeOrg.name} className="w-6 h-6 rounded-md object-cover" />
                ) : (
                    <span className="material-icons-round p-1 bg-gray-100 rounded-md">corporate_fare</span>
                )}
                <span className="material-icons-round text-gray-600">unfold_more</span>
            </button>
            {isOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl z-20 py-1">
                    {orgs.map(org => (
                        <button 
                            key={org.id} 
                            onClick={() => { onSwitchOrg(org.id); setIsOpen(false); }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-3 disabled:opacity-50"
                            disabled={org.id === activeOrgId}
                        >
                            {org.photoUrl ? (
                                <img src={org.photoUrl} alt={org.name} className="w-6 h-6 rounded-md object-cover" />
                            ) : (
                                <span className="material-icons-round p-1 bg-gray-100 rounded-md">corporate_fare</span>
                            )}
                            <span className="flex-1 truncate">{org.name}</span>
                            {org.id === activeOrgId && <span className="material-icons-round text-green-500">check_circle</span>}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};


export const DropdownMenu = ({ onEdit, onDelete }: { onEdit: () => void; onDelete: () => void }) => {
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div ref={menuRef}>
            <button onClick={() => setIsOpen(!isOpen)} className="p-2 text-gray-400 hover:bg-gray-100 rounded-full">
                <span className="material-icons-round">more_vert</span>
            </button>
            {isOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl z-20 py-1">
                    <button onClick={() => { onEdit(); setIsOpen(false); }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2">
                        <span className="material-icons-round text-base">edit</span> Edit
                    </button>
                    <button onClick={() => { onDelete(); setIsOpen(false); }} className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 flex items-center gap-2">
                        <span className="material-icons-round text-base">delete</span> Delete
                    </button>
                </div>
            )}
        </div>
    );
};

export const AttachmentButton = ({ onImageSelect }: { onImageSelect: (dataUrl: string) => void }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const validation = validateChatAttachmentFile(file);
      if (!validation.valid) {
        alert(validation.error);
        if (event.target) event.target.value = '';
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        onImageSelect(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <>
      <input 
        type="file" 
        accept="image/jpeg,image/png,image/webp,image/gif,application/pdf" 
        ref={fileInputRef} 
        onChange={handleFileChange} 
        className="hidden" 
      />
      <button 
        type="button"
        onClick={() => fileInputRef.current?.click()} 
        title="Attach photo or PDF (max 5MB)"
        className="p-3 text-gray-500 hover:bg-gray-100 rounded-full"
      >
        <span className="material-icons-round">attach_file</span>
      </button>
    </>
  );
};

export const FileAttachmentButton = ({ onFileSelect }: { onFileSelect: (file: { dataUrl: string; name: string; type: string }) => void }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const validation = validateAttachmentFile(file, {
        maxSizeBytes: 10 * 1024 * 1024,
        allowedExtensionsText: 'Please attach images (JPG, PNG, WEBP), PDFs, or Word documents (.doc, .docx) under 10MB.'
      });
      if (!validation.valid) {
        alert(validation.error);
        if (event.target) event.target.value = '';
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        onFileSelect({
          dataUrl: e.target?.result as string,
          name: file.name,
          type: file.type,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <>
      <input 
        type="file" 
        ref={fileInputRef} 
        onChange={handleFileChange} 
        className="hidden" 
        accept="image/jpeg,image/png,image/webp,application/pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      />
      <button type="button" onClick={() => fileInputRef.current?.click()} className="flex items-center gap-2 text-sm font-medium text-indigo-600 hover:text-indigo-800 p-2 rounded-lg hover:bg-indigo-50 -ml-2">
        <span className="material-icons-round">attach_file</span>
        Attach File
      </button>
    </>
  );
};

export const VoiceInput = ({ onTranscriptReceived, onProcessingChange, className = '' }: { onTranscriptReceived: (transcript: string) => void, onProcessingChange: (isProcessing: boolean) => void, className?: string }) => {
    const [isRecording, setIsRecording] = useState(false);
    const [permissionError, setPermissionError] = useState(false);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const timeoutRef = useRef<any>(null); // For max duration

    const SYSTEM_INSTRUCTION_TRANSCRIBER = `You are a transcription service. Transcribe the user's audio input accurately. Do not add any extra commentary, conversational text, or introductory phrases. Only return the transcribed text.`;

    const stopRecording = () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
            mediaRecorderRef.current.stop();
            // Stop all tracks to turn off the mic indicator
            streamRef.current?.getTracks().forEach(track => track.stop());
            streamRef.current = null;
            setIsRecording(false);
        }
    };

    const startRecording = async () => {
        if (isRecording) return;
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            setPermissionError(false);
            const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
            mediaRecorderRef.current = recorder;
            
            const chunks: Blob[] = [];
            recorder.ondataavailable = e => chunks.push(e.data);

            recorder.onstop = async () => {
                // Keep processing true while we transcribe
                const audioBlob = new Blob(chunks, { type: 'audio/webm' });
                const reader = new FileReader();
                reader.readAsDataURL(audioBlob);
                reader.onloadend = async () => {
                    const base64Audio = (reader.result as string).split(',')[1];
                    try {
                        // Use the retry logic here
                        const response = await generateContentWithRetry({
                            model: 'gemini-3-flash-preview',
                            contents: [{
                                parts: [{ inlineData: { mimeType: 'audio/webm', data: base64Audio } }, {text: "Transcribe this audio."}]
                            }],
                            config: { systemInstruction: SYSTEM_INSTRUCTION_TRANSCRIBER }
                        });
                        onTranscriptReceived(response.text || '');
                    } catch (error: any) {
                        console.error("Transcription failed", error);
                        // Check if it's the known API Key Referrer error from generateContentWithRetry
                        if (error.message && error.message.includes("API Key Restriction Error")) {
                            alert(error.message);
                        } else {
                            // Show generic alert but log real error
                            alert(`Failed to transcribe audio. Error: ${error.message || 'Unknown'}`);
                        }
                    } finally {
                        onProcessingChange(false); // End processing after transcription
                    }
                };
            };
            
            recorder.start();
            setIsRecording(true);
            onProcessingChange(true); // Start processing when recording starts
            
            // Limit recording to 60 seconds to avoid huge payloads
            timeoutRef.current = setTimeout(() => {
                if (recorder.state === "recording") {
                    stopRecording();
                    alert("Maximum recording time (60s) reached.");
                }
            }, 60000);

        } catch (error) {
            console.error("Mic access denied:", error);
            setPermissionError(true);
            alert("To record a voice note, EduGravity needs access to your microphone. Please enable microphone permissions in your browser's site settings.");
            onProcessingChange(false);
        }
    };

    const handleMicClick = () => {
        if (isRecording) {
            stopRecording();
        } else {
            startRecording();
        }
    }

    return (
        <button onClick={handleMicClick} className={`${className} p-2 rounded-full ${isRecording ? 'bg-red-500 text-white animate-pulse' : 'bg-gray-200 text-gray-600'}`}>
            <span className="material-icons-round text-lg">{permissionError ? 'mic_off' : 'mic'}</span>
        </button>
    );
};


export const LoadingScreen = () => (
    <div className="h-screen w-full flex items-center justify-center bg-white">
      <div className="flex flex-col items-center">
        <span className="material-icons-round text-5xl text-indigo-500 animate-spin">autorenew</span>
        <p className="mt-4 text-gray-500">Loading...</p>
      </div>
    </div>
);

export const CopyButton = ({ textToCopy }: { textToCopy: string }) => {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
        navigator.clipboard.writeText(textToCopy);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };
    return (
        <button onClick={handleCopy} className="p-2 text-gray-400 hover:bg-gray-200 rounded-full ml-2">
            <span className="material-icons-round text-lg">{copied ? 'check' : 'content_copy'}</span>
        </button>
    );
};

export type TabMenuItem = {
  id: string;
  label: string;
  icon: string;
  badge?: string | number;
  badgeColor?: string;
  dot?: boolean;
  dotColor?: string;
};

export const TabMenu: FC<{
  items: TabMenuItem[];
  activeTab: string;
  onSelectTab: (tabId: string) => void;
}> = ({ items, activeTab, onSelectTab }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 5); // 5px tolerance
    }
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, [items]);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
        const scrollAmount = scrollRef.current.clientWidth / 2;
        scrollRef.current.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
        setTimeout(checkScroll, 300);
    }
  };

  return (
    <div className="no-print border-b border-gray-200 bg-white relative flex items-center">
      {showLeftArrow && (
        <button
          onClick={() => scroll('left')}
          className="absolute left-0 z-10 h-full px-2 bg-gradient-to-r from-white via-white to-transparent text-gray-600 hover:text-indigo-600 flex items-center"
        >
          <span className="material-icons-round">chevron_left</span>
        </button>
      )}
      
      <div 
        ref={scrollRef}
        className="flex overflow-x-auto scrollbar-hide flex-1 scroll-smooth"
        onScroll={checkScroll}
      >
        <div className="flex min-w-full">
            {items.map(item => (
            <button
                key={item.id}
                onClick={() => {
                    onSelectTab(item.id);
                }}
                className={`flex-1 flex flex-col items-center justify-center gap-1 p-3 text-xs font-bold whitespace-nowrap transition-colors min-w-[80px] select-none relative ${
                activeTab === item.id
                    ? 'text-indigo-600 border-b-2 border-indigo-600'
                    : 'text-gray-500 hover:bg-gray-100'
                }`}
            >
                <div className="relative inline-flex items-center justify-center">
                    <span className="material-icons-round !text-xl">{item.icon}</span>
                    {item.badge !== undefined && item.badge !== null && item.badge !== '' && item.badge !== 0 && (
                        <span className={`absolute -top-1.5 -right-2.5 px-1.5 py-0.2 min-w-[16px] h-4 rounded-full text-[10px] font-bold flex items-center justify-center text-white shadow-2xs ${item.badgeColor || 'bg-rose-500'}`}>
                            {item.badge}
                        </span>
                    )}
                    {item.dot && (!item.badge || item.badge === 0) && (
                        <span className={`absolute -top-0.5 -right-1 w-2.5 h-2.5 rounded-full ring-2 ring-white ${item.dotColor || 'bg-rose-500'}`} />
                    )}
                </div>
                <span>{item.label}</span>
            </button>
            ))}
        </div>
      </div>

      {showRightArrow && (
        <button
          onClick={() => scroll('right')}
          className="absolute right-0 z-10 h-full px-2 bg-gradient-to-l from-white via-white to-transparent text-gray-600 hover:text-indigo-600 flex items-center"
        >
          <span className="material-icons-round">chevron_right</span>
        </button>
      )}
    </div>
  );
};
